/* eslint-disable no-use-before-define */

import groupBy from 'lodash/groupBy';

import { mergeSignals } from '@lumapps/utils/browser/mergeSignals';
import { nextFrame } from '@lumapps/utils/browser/nextFrame';
import { mergeFields } from '@lumapps/utils/object/mergeFields';
import { Fields } from '@lumapps/utils/types/Fields';

/**
 * Wrap an API get multi async function to batch multiple get calls.
 *
 * Batching is done within a frame. All subsequent call to get() will be moved to a new batch
 */
export function createGetMultiBatcher<I extends object>(options: {
    /** Async API get multi */
    getMulti: (ids: string[], fields: Fields<I>, signal?: AbortSignal) => Promise<I[]>;
    /** Field or function selector to get the ID from the item */
    getId: keyof I | ((item: I) => string);
}) {
    const pendingRequests: Array<{
        id: string;
        fields: Fields<I>;
        resolve(item: I | undefined): void;
        reject(reason?: any): void;
        signal?: AbortSignal;
    }> = [];

    /** Run batches of requests currently pending */
    async function runBatches() {
        if (!pendingRequests.length) {
            return;
        }
        // Resolve id by function of field name
        const getId =
            typeof options.getId === 'function' ? options.getId : (item: I) => String((item as any)[options.getId]);

        const processingRequests = [...pendingRequests];
        // Remove the requests that will be processed
        pendingRequests.splice(0, pendingRequests.length);

        // Regroups requests by id
        const requestsById = groupBy(processingRequests, (request) => request.id);

        // Batch call API get multi
        const ids = Object.keys(requestsById);
        const fields = mergeFields(processingRequests.map((r) => r.fields));
        const signal = mergeSignals(processingRequests.map((r) => r.signal));

        try {
            const response = await options.getMulti(ids, fields, signal);

            // Resolve requests
            for (const item of response) {
                const id = getId(item);
                const requests = requestsById[id];
                for (const request of requests || []) {
                    request.resolve(item);
                }
                delete requestsById[id];
            }

            // Remaining unresolved request are items that do not exist anymore
            const unresolvedRequests = Object.values(requestsById).flat();
            for (const request of unresolvedRequests) {
                request.resolve(undefined);
            }
        } catch (error) {
            // Reject requests
            for (const request of processingRequests) {
                request.reject(error);
            }
        }

        // If we have still some pending requests => schedule a new run
        scheduleNextBatch();
    }

    /** Schedule next batch if needed (requests are pending). */
    async function scheduleNextBatch() {
        if (!pendingRequests.length) {
            return;
        }
        // Run batch on next frame
        await nextFrame();
        runBatches();
    }

    /**
     * Get item by ID (scheduled in a batch).
     * **Warning**: providing different params might trigger multiple batch
     */
    return function getBatched(id: string, fields: Fields<I>, signal?: AbortSignal): Promise<I | undefined> {
        return new Promise<I | undefined>((resolve, reject) => {
            pendingRequests.push({ id, fields, signal, resolve, reject });

            // Schedule next batch
            scheduleNextBatch();
        });
    };
}
